import React, { useState, useEffect, useMemo } from 'react';
import { FormGroup } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import { useHistory, useLocation, Link } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import Select from 'react-select';
import { uniqBy, orderBy } from 'lodash';
import { useTranslation } from 'react-i18next';
import Loader from '../../../../Components/Utility/Loader/Loader';
import BtnDelete from '../../../../Components/Utility/FilterAction/BtnDelete';
import BomLibraryCard from '../../../../Components/Card/BomLibraryCard/BomLibraryCard';
import { SelectBoxStyles } from '../../../../Components/Utility/Controls/SelectControl';
import * as action from '../store/BomLibrary.action';
import * as systemMasterAction from '../../../SystemMasters/Store/SystemMasters.action';
import { toasterMsg } from '../../../../Components/Toaster/Toaster';
import DragDropSort from '../../../../Components/DragDropSort/DragDropSort';
import BtnSort from '../../../../Components/Utility/FilterAction/BtnSort';
import { customStylesBom } from '../../../../Components/helper';
import { useComponent } from '../../../UserSetting/UsersLibrary/UserRights';
import BomLibraryTagsModel from '../bomLibraryTags/bomLibraryTagsModel';

const sortByOptions = [
  {
    value: 'asc',
    propertyName: 'referenceCode',
    label: 'Component Code (Ascending)',
  },
  {
    value: 'desc',
    propertyName: 'referenceCode',
    label: 'Component Code (Descending)',
  },
];

function AddEditBomLibraryGroup({ match }) {
  const history = useHistory();
  const location = useLocation();
  const { t } = useTranslation('common');
  const dispatch = useDispatch();
  const libraryBomData = useSelector((redState) => redState);
  const { UserDetails, BomLibraryDetails, Users, SystemMastersDetails } = libraryBomData;
  const { userInfo } = UserDetails;
  const { logInRights } = Users;
  const { SeasonList, YearsList } = BomLibraryDetails;
  const { category1List, category2List, category3List, pcTypeList } = SystemMastersDetails;
  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm();

  const [addBomTagsModal, setAddBomTagsModal] = useState(false);
  const [selectedTagId, setSelectedTagId] = useState();
  const [showLoader, setShowLoader] = useState(false);
  const [editForm, setEditForm] = useState(match.path === '/libraries/bom-library-group/edit/:id/:groupId');

  const { CheckActionPermission } = useComponent();
  const BomLibraryAddEditAccess = CheckActionPermission(logInRights, 'libraries', 'bom', 'addEdit');
  const BomLibraryDeleteAccess = CheckActionPermission(logInRights, 'libraries', 'bom', 'delete');

  const [bomLibraryList, setBomLibraryList] = useState([]);
  const [selectedBomLibraries, setSelectBomLibraries] = useState([]);
  const [selectedIdArray, setSelectedIdArray] = useState([]);
  const [showDragDrop, setShowDragDrop] = useState(false);
  const [sortOrder, setSortOrder] = useState('asc');
  const [sortValue, setSortValue] = useState('sequence');
  const [placeHolderName, setPlaceHolderName] = useState('');
  const [catBasedOnPTData, setCatBasedOnPTData] = useState(false);

  const devOrderId = match?.params?.devOrderId;

  const [state, setState] = useState({
    seasonId: null,
    year: null,
    productType: null,
    catOneId: null,
    catTwoId: null,
    catThreeId: null,
    sortBy: null,
    sorOrder: 'Asc',
  });

  const hideShowDragDrop = (e) => {
    e.preventDefault();
    setShowDragDrop(!showDragDrop);
  };

  const handleSortBy = async (val) => {
    if (val?.propertyName) {
      setSortValue(val.propertyName);
    }
    if (val?.value) {
      setSortOrder(val.value);
    }

    setState((prev) => ({
      ...prev,
      sortBy: val,
      selectedBomLibraries: [],
      loading: true,
    }));

    const params = {
      groupId: match.params.groupId,
    };
    await dispatch(action.fetchBomLibraryGroupByDetailId(params));
  };

  const handleDropdownChange = (val, type) => {
    // Base update
    let next = { ...state, [type]: val };

    if (catBasedOnPTData) {
      // Reset downstream fields when dependencies change
      if (type === 'productType') {
        next = { ...next, catOneId: null, catTwoId: null, catThreeId: null };
        setValue('catOneId', null, { shouldValidate: true });
        setValue('catTwoId', null, { shouldValidate: true });
        setValue('catThreeId', null, { shouldValidate: true });
      } else if (type === 'catOneId') {
        next = { ...next, catTwoId: null, catThreeId: null };
        setValue('catTwoId', null, { shouldValidate: true });
        setValue('catThreeId', null, { shouldValidate: true });
      } else if (type === 'catTwoId') {
        next = { ...next, catThreeId: null };
        setValue('catThreeId', null, { shouldValidate: true });
      }
    }

    setState(next);
    setValue(type, val, {
      shouldValidate: true,
    });
  };

  // Filtered options when Product Type based dependency is enabled
  const filteredCategory1Options = useMemo(() => {
    if (!catBasedOnPTData) return category1List || [];
    const selectedPTId = state?.productType?._id || state?.productType?.value;
    if (!selectedPTId) return [];
    return (category1List || []).filter((c1) => {
      const ptField = c1?.productType || c1?.productTypes || [];
      const ids = Array.isArray(ptField) ? ptField : [];
      return ids.includes(selectedPTId);
    });
  }, [catBasedOnPTData, category1List, state?.productType]);

  const filteredCategory2Options = useMemo(() => {
    if (!catBasedOnPTData) return category2List || [];
    const selectedCat1Id = state?.catOneId?._id;
    if (!selectedCat1Id) return [];
    return (category2List || []).filter((c2) => Array.isArray(c2?.catOneIds) && c2.catOneIds.includes(selectedCat1Id));
  }, [catBasedOnPTData, category2List, state?.catOneId]);

  const filteredCategory3Options = useMemo(() => {
    if (!catBasedOnPTData) return category3List || [];
    if (!state?.catOneId || !state?.catTwoId) return [];
    return category3List || [];
  }, [catBasedOnPTData, category3List, state?.catOneId, state?.catTwoId]);

  const redirectToListPage = () => {
    history.push(`/libraries/bom-library-group/${match.params.id}`);
  };

  const fetchDataById = async (preselectedBomLibs) => {
    const params = {
      groupId: match.params.groupId,
      bomTypeId: match.params.id,
    };
    const res = await dispatch(action.fetchBomLibraryGroupByDetailId(params, t));
    if (res) {
      const preselectedBomLibsData = [...preselectedBomLibs, ...(res.BOMLibs || [])];
      const result = uniqBy(preselectedBomLibsData, '_id');
      setPlaceHolderName(res?.bomTypeName);
      setSelectBomLibraries(result);
      const ptRaw = res?.productType || res?.productTypeId || null;
      const ptId = ptRaw?._id || ptRaw || null;
      const productTypeVal = ptId ? (pcTypeList || []).find((pt) => pt?._id === ptId) || { _id: ptId, name: res?.productType?.name || '' } : null;

      const stateObj = {
        seasonId: res.seasonId ? res.seasonId : null,
        year: res.year ? { value: res.year, label: res.year } : null,
        catOneId: res.catOne,
        catTwoId: res.catTwo,
        catThreeId: res.catThree,
        productType: productTypeVal,
      };
      const obj = {
        ...stateObj,
        name: res.name,
        description: res.description,
      };
      for (const [key, value] of Object.entries(obj)) {
        setValue(key, value);
      }
      setState({ ...stateObj });
    } else {
      redirectToListPage();
    }
  };

  const getBomLibraryList = async () => {
    const obj = { isAllRecord: true, bomTypeId: match.params.id };
    const res = await dispatch(action.fetchBomLibraryListData(obj));
    return res.data || [];
  };

  useEffect(() => {
    const fetchCatBasedOnPTData = async () => {
      try {
        const res = await dispatch(systemMasterAction.getCatBasedOnPT());
        setCatBasedOnPTData(!!res);
      } catch (e) {
        setCatBasedOnPTData(false);
      }
    };
    fetchCatBasedOnPTData();
  }, [dispatch]);

  useEffect(() => {
    dispatch(
      systemMasterAction.getPCTypesDetails(
        {
          loggedInUserId: userInfo?.userId,
          isActive: true,
          tier: 'Tier1',
        },
        true,
      ),
    );
  }, [dispatch, userInfo?.userId]);

  useEffect(() => {
    async function fetchAPI() {
      await dispatch(action.fetchSeason({ isActive: 1, loggedInUserId: userInfo.userId }));
      dispatch(
        systemMasterAction.getCategory1Details({
          categoryType: 1,
          loggedInUserId: userInfo?.userId,
          isActive: true,
        }),
      );
      dispatch(
        systemMasterAction.getCategory2Details({
          categoryType: 2,
          loggedInUserId: userInfo?.userId,
          isActive: true,
        }),
      );
      const bomLibaries = await getBomLibraryList();
      setBomLibraryList(bomLibaries);
      let preselectedBomLibs = [];
      if (location?.state?.libIds) {
        const libIds = [...location.state.libIds];
        preselectedBomLibs = bomLibaries.filter((item) => libIds.includes(item._id));
        setEditForm(false);
      }
      if (editForm) {
        await fetchDataById(preselectedBomLibs);
      } else {
        setSelectBomLibraries(preselectedBomLibs);
      }
      setShowLoader(false);
    }
    fetchAPI();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // effect
    const filtered = selectedBomLibraries?.length
      ? selectedBomLibraries.map((item) => ({
          ...item,
          // _id: item.libId,
          sequence: item?.sequence ? item.sequence : 0,
        }))
      : [];

    setState((prev) => ({
      ...prev,
      selectedBomLibraries: filtered,
      loading: false,
    }));
    return () => {
      // cleanup
      setState((prev) => ({ ...prev, selectedBomLibraries: [] }));
    };
  }, [selectedBomLibraries]);

  useEffect(() => {
    if (state.catOneId && state.catTwoId) {
      const params = {
        categoryOneId: state.catOneId._id,
        categoryTwoId: state.catTwoId._id,
        categoryType: 3,
        isActive: true,
        loggedInUserId: userInfo.userId,
      };
      if (!editForm) {
        handleDropdownChange(null, 'catThreeId');
      }
      dispatch(systemMasterAction.getCategory3Details(params));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.catOneId, state.catTwoId]);

  const onSubmitMethod = async (data) => {
    setShowLoader(true);
    const payload = {
      ...data,
      seasonId: data?.seasonId?._id ? data.seasonId._id : null,
      year: data?.year?.value ? data.year.value : null,
      productType: data?.productType?._id ? data.productType._id : null,
      catOneId: data?.catOneId?._id ? data.catOneId._id : null,
      catTwoId: data?.catTwoId?._id ? data.catTwoId._id : null,
      catThreeId: data?.catThreeId?._id ? data.catThreeId._id : null,
      bomType: match.params.id ? match.params.id : '',
      BOMLibIds: selectedBomLibraries.map((item) => item._id),
    };

    let res;
    if (match.params.groupId) {
      payload.groupId = match.params.groupId;
      res = await dispatch(action.editBomLibraryGroup(payload, t));
    } else {
      res = await dispatch(action.addBomLibraryGroup(payload, t));
    }
    if (res) {
      redirectToListPage();
    }
    setShowLoader(false);
  };

  const handleBomLibraryDropDown = (val) => {
    const bomLibs = [...selectedBomLibraries];
    const isBomExist = bomLibs.find((item) => item._id === val._id);
    if (isBomExist) {
      toasterMsg(t('BOM_LIBRARY.BOM_LIBRARY_GROUP.ADD_EDIT_BOM_LIBRARY_GROUP.BOM_LIBRARY_ADDED'), 'danger');
    } else {
      bomLibs.push(val);
      setSelectBomLibraries(bomLibs);
    }
  };

  const onSelectCheckBox = async (_e, id) => {
    let selectedItems = [...selectedIdArray];
    const findItems = selectedItems.filter((item) => item === id);
    if (findItems.length > 0) {
      selectedItems = selectedItems.filter((item) => item !== id);
      setSelectedIdArray([...selectedItems]);
    } else {
      setSelectedIdArray([...selectedItems, id]);
    }
  };

  const handleSaveSequence = async (e, sequenceList) => {
    setSortValue('sequence');
    setSortOrder('asc');
    const { groupId } = match.params;
    const list = {
      sequence: sequenceList.sequence,
    };
    dispatch(action.saveSequence(groupId, list, t));
    const bomLibaries = await getBomLibraryList();
    setBomLibraryList(bomLibaries);
    let preselectedBomLibs = [];
    if (location?.state?.libIds) {
      const libIds = [...location.state.libIds];
      preselectedBomLibs = bomLibaries.filter((item) => libIds.includes(item._id));
    }
    fetchDataById(preselectedBomLibs);

    hideShowDragDrop(e);
  };

  const onDeleteBomLibs = (id) => {
    let selectedItems = [...selectedIdArray];
    let bomLibs = [...selectedBomLibraries];
    if (id) {
      selectedItems = selectedItems.filter((item) => item !== id);
      setSelectedIdArray([...selectedItems]);
      bomLibs = bomLibs.filter((item) => item._id !== id);
      setSelectBomLibraries(bomLibs);
    } else {
      bomLibs = bomLibs.filter((item) => !selectedItems.includes(item._id));
      setSelectedIdArray([]);
      setSelectBomLibraries(bomLibs);
    }
  };

  const toggleBomTagsModel = () => {
    setAddBomTagsModal(!addBomTagsModal);
  };

  const onAddBomTags = (id) => {
    setAddBomTagsModal(true);
    setSelectedTagId(id);
  };

  return (
    <>
      <div className="pb-5">
        <div className="page-title-filter sticky">
          <div className="title-container">
            <h4 className="title">{t('BOM_LIBRARY.BOM_LIBRARY_GROUP.ADD_EDIT_BOM_LIBRARY_GROUP.LIBRARY_GROUP')}</h4>
          </div>
          <div className="search-filter">
            <div className="web-filter">
              {editForm && BomLibraryAddEditAccess && (
                <button
                  className="btn btn-white"
                  type="button"
                  onClick={() => {
                    setEditForm(false);
                  }}
                >
                  <i className="icon-edit mr-1" />
                  {t('BOM_LIBRARY.BOM_LIBRARY_GROUP.ADD_EDIT_BOM_LIBRARY_GROUP.EDIT')}
                </button>
              )}
              {!editForm && (BomLibraryAddEditAccess || BomLibraryDeleteAccess) && (
                <button className="btn btn-white" type="button" onClick={handleSubmit(onSubmitMethod)}>
                  <i className="icon-Save mr-1" />
                  {t('BOM_LIBRARY.BOM_LIBRARY_GROUP.ADD_EDIT_BOM_LIBRARY_GROUP.SAVE')}
                </button>
              )}
              <Link className="cursor-pointer text-dark text-decoration-none btn btn-white ml-2" to={`/libraries/bom-library-group/${match.params.id}`}>
                <i className="icon-close mr-1" />
                {t('BOM_LIBRARY.BOM_LIBRARY_GROUP.ADD_EDIT_BOM_LIBRARY_GROUP.CANCEL')}
              </Link>
            </div>
          </div>
        </div>

        <div className="container-fluid">
          <form className={editForm ? 'disabled-section' : ''}>
            {showLoader && <Loader />}
            {!showLoader && (
              <>
                <div className="row mt-4 mb-3">
                  <div className="col-lg-3">
                    <FormGroup>
                      <div className="input-group form-floating-label fixed-label">
                        <input
                          type="text"
                          name="name"
                          placeholder={t('BOM_LIBRARY.BOM_LIBRARY_GROUP.ADD_EDIT_BOM_LIBRARY_GROUP.GROUP_NAME')}
                          {...register('name', {
                            required: true,
                            validate: (value) => !!value.trim() || 'no whitespaces.',
                          })}
                          className="form-control"
                        />
                        <label htmlFor="grp_name" className="asterisk-req">
                          {t('BOM_LIBRARY.BOM_LIBRARY_GROUP.ADD_EDIT_BOM_LIBRARY_GROUP.GROUP_NAME')}
                          <span>*</span>
                        </label>
                      </div>
                      {errors.name && (
                        <small className="error-message">{t('BOM_LIBRARY.BOM_LIBRARY_GROUP.ADD_EDIT_BOM_LIBRARY_GROUP.GROUP_NAME_REQUIRED')}</small>
                      )}
                    </FormGroup>
                  </div>

                  <div className="col-lg-9">
                    <FormGroup>
                      <div className="input-group form-floating-label fixed-label">
                        <input
                          type="text"
                          name="description"
                          placeholder={t('BOM_LIBRARY.BOM_LIBRARY_GROUP.ADD_EDIT_BOM_LIBRARY_GROUP.GROUP_DESCRIPTION')}
                          {...register('description', { required: false })}
                          className="form-control"
                        />
                        <label htmlFor="grp_name" className="asterisk-req">
                          {t('BOM_LIBRARY.BOM_LIBRARY_GROUP.ADD_EDIT_BOM_LIBRARY_GROUP.GROUP_DESCRIPTION')}
                        </label>
                      </div>
                    </FormGroup>
                  </div>

                  <div className="col-lg-3">
                    <FormGroup>
                      <div className="select-group">
                        <div className="common-react-select">
                          <label htmlFor="grp_name" className="field-lbl asterisk-req">
                            {t('BOM_LIBRARY.BOM_LIBRARY_GROUP.ADD_EDIT_BOM_LIBRARY_GROUP.SEASON')}
                            {/* <span>*</span> */}
                          </label>
                          <Select
                            {...register('seasonId', { required: false })}
                            name="seasonId"
                            placeholder={t('BOM_LIBRARY.BOM_LIBRARY_GROUP.ADD_EDIT_BOM_LIBRARY_GROUP.SELECT_SEASON')}
                            options={SeasonList}
                            value={state.seasonId}
                            onChange={(val) => {
                              handleDropdownChange(val, 'seasonId');
                            }}
                            classNamePrefix="common-select"
                            getOptionLabel={(option) => option.name}
                            getOptionValue={(option) => option._id}
                            styles={SelectBoxStyles}
                          />
                        </div>
                      </div>
                      {errors.seasonId && (
                        <small className="error-message">{t('BOM_LIBRARY.BOM_LIBRARY_GROUP.ADD_EDIT_BOM_LIBRARY_GROUP.SEASON_REQUIRED')}</small>
                      )}
                    </FormGroup>
                  </div>

                  <div className="col-lg-3">
                    <FormGroup>
                      <div className="select-group">
                        <div className="common-react-select">
                          <label htmlFor="grp_name" className="field-lbl asterisk-req">
                            {t('BOM_LIBRARY.BOM_LIBRARY_GROUP.ADD_EDIT_BOM_LIBRARY_GROUP.YEAR')}
                            {/* <span>*</span> */}
                          </label>
                          <Select
                            {...register('year', { required: false })}
                            name="year"
                            placeholder={t('BOM_LIBRARY.BOM_LIBRARY_GROUP.ADD_EDIT_BOM_LIBRARY_GROUP.SELECT_YEAR')}
                            options={YearsList}
                            value={state.year}
                            onChange={(val) => {
                              handleDropdownChange(val, 'year');
                            }}
                            classNamePrefix="common-select"
                            styles={SelectBoxStyles}
                          />
                        </div>
                      </div>
                      {errors.year && <small className="error-message"> {t('BOM_LIBRARY.BOM_LIBRARY_GROUP.ADD_EDIT_BOM_LIBRARY_GROUP.YEAR_REQUIRED')}</small>}
                    </FormGroup>
                  </div>
                  {catBasedOnPTData && (
                    <div className="col-lg-3">
                      <FormGroup>
                        <div className="select-group">
                          <div className="common-react-select">
                            <label htmlFor="grp_name" className="field-lbl asterisk-req">
                              Product Type
                              {/* <span>*</span> */}
                            </label>
                            <Select
                              {...register('productType', { required: false })}
                              name="productType"
                              placeholder="Select ProductType"
                              options={pcTypeList}
                              value={state.productType}
                              onChange={(val) => {
                                handleDropdownChange(val, 'productType');
                              }}
                              classNamePrefix="common-select"
                              getOptionLabel={(option) => option.name}
                              getOptionValue={(option) => option._id}
                              styles={SelectBoxStyles}
                            />
                          </div>
                        </div>
                        {errors.productType && <small className="error-message">Product type required</small>}
                      </FormGroup>
                    </div>
                  )}
                  <div className="col-lg-3">
                    <FormGroup>
                      <div className="select-group">
                        <div className="common-react-select">
                          <label htmlFor="grp_name" className="field-lbl asterisk-req">
                            {t('BOM_LIBRARY.BOM_LIBRARY_GROUP.ADD_EDIT_BOM_LIBRARY_GROUP.CATEGORY_1')}
                            {/* <span>*</span> */}
                          </label>
                          <Select
                            {...register('catOneId', { required: false })}
                            name="catOneId"
                            placeholder={t('BOM_LIBRARY.BOM_LIBRARY_GROUP.ADD_EDIT_BOM_LIBRARY_GROUP.SELECT_CATEGORY_1')}
                            options={catBasedOnPTData ? filteredCategory1Options : category1List}
                            value={state.catOneId}
                            onChange={(val) => {
                              handleDropdownChange(val, 'catOneId');
                            }}
                            classNamePrefix="common-select"
                            getOptionLabel={(option) => option.name}
                            getOptionValue={(option) => option._id}
                            styles={SelectBoxStyles}
                          />
                        </div>
                      </div>
                      {errors.catOneId && (
                        <small className="error-message">{t('BOM_LIBRARY.BOM_LIBRARY_GROUP.ADD_EDIT_BOM_LIBRARY_GROUP.CATEGORY_1_REQUIRED')}</small>
                      )}
                    </FormGroup>
                  </div>

                  <div className="col-lg-3">
                    <FormGroup>
                      <div className="select-group">
                        <div className="common-react-select">
                          <label htmlFor="grp_name" className="field-lbl asterisk-req">
                            {t('BOM_LIBRARY.BOM_LIBRARY_GROUP.ADD_EDIT_BOM_LIBRARY_GROUP.CATEGORIES_2')}
                            {/* <span>*</span> */}
                          </label>
                          <Select
                            {...register('catTwoId', { required: false })}
                            name="catTwoId"
                            placeholder={t('BOM_LIBRARY.BOM_LIBRARY_GROUP.ADD_EDIT_BOM_LIBRARY_GROUP.SELECT_CATEGORY_2')}
                            options={catBasedOnPTData ? filteredCategory2Options : category2List}
                            value={state.catTwoId}
                            onChange={(val) => {
                              handleDropdownChange(val, 'catTwoId');
                            }}
                            classNamePrefix="common-select"
                            getOptionLabel={(option) => option.name}
                            getOptionValue={(option) => option._id}
                            styles={SelectBoxStyles}
                          />
                        </div>
                      </div>
                      {errors.catTwoId && (
                        <small className="error-message">{t('BOM_LIBRARY.BOM_LIBRARY_GROUP.ADD_EDIT_BOM_LIBRARY_GROUP.CATEGORY_2_REQUIRED')}</small>
                      )}
                    </FormGroup>
                  </div>

                  <div className="col-lg-3">
                    <FormGroup>
                      <div className="select-group">
                        <div className="common-react-select">
                          <label htmlFor="grp_name" className="field-lbl asterisk-req">
                            {t('BOM_LIBRARY.BOM_LIBRARY_GROUP.ADD_EDIT_BOM_LIBRARY_GROUP.CATEGORY_3')}
                          </label>
                          <Select
                            {...register('catThreeId', { required: false })}
                            name="catThreeId"
                            placeholder={t('BOM_LIBRARY.BOM_LIBRARY_GROUP.ADD_EDIT_BOM_LIBRARY_GROUP.SELECT_CATEGORY_3')}
                            options={filteredCategory3Options}
                            isClearable
                            value={state.catThreeId}
                            onChange={(val) => {
                              handleDropdownChange(val, 'catThreeId');
                            }}
                            classNamePrefix="common-select"
                            getOptionLabel={(option) => option.name}
                            getOptionValue={(option) => option._id}
                            styles={SelectBoxStyles}
                          />
                        </div>
                      </div>
                    </FormGroup>
                  </div>
                </div>
                <div className="row">
                  <div className="col-lg-4">
                    <div className="d-flex mb-3">
                      <div className="custom-react-select flex-fill">
                        <Select
                          name="bomLibrary"
                          placeholder={`${placeHolderName} library...`}
                          options={bomLibraryList}
                          onChange={handleBomLibraryDropDown}
                          value={[]}
                          getOptionLabel={(option) => option.referenceCode}
                          styles={customStylesBom}
                        />
                      </div>
                      {BomLibraryDeleteAccess && (
                        <div className="btn-outline-container">
                          <BtnDelete
                            multiDelete={() => {
                              onDeleteBomLibs(null);
                            }}
                            isDisabled={selectedIdArray.length <= 0}
                          />
                        </div>
                      )}
                      {/* disable={!editForm} */}
                      {BomLibraryAddEditAccess && (
                        <div>
                          {selectedBomLibraries.length > 0 && (
                            <button
                              className="btn btn-outline"
                              onClick={hideShowDragDrop}
                              type="button"
                              aria-label="rearrange"
                              disabled={match.path.includes('add')}
                            >
                              <i className="icon-expand" />
                            </button>
                          )}
                        </div>
                      )}
                      {BomLibraryAddEditAccess && (
                        <div className="btn-outline-container">
                          <BtnSort options={sortByOptions} onBtnClick={handleSortBy} val={state.sortBy} disable={match.path.includes('add')} />
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="row bomcard-row">
                  {selectedBomLibraries &&
                    selectedBomLibraries.length > 0 &&
                    orderBy(selectedBomLibraries, [sortValue], [sortOrder]).map((item, i) => (
                      <BomLibraryCard
                        editeble={false}
                        key={item._id}
                        item={item}
                        index={i}
                        onCheck={onSelectCheckBox}
                        onDelete={onDeleteBomLibs}
                        selectedIds={selectedIdArray}
                        onAddBomTags={onAddBomTags}
                      />
                    ))}
                </div>
              </>
            )}
          </form>
        </div>
      </div>

      {showDragDrop ? (
        <DragDropSort
          devOrderId={devOrderId}
          itemList={selectedBomLibraries}
          isShow={showDragDrop}
          isToggle={(e) => hideShowDragDrop(e)}
          saveSequence={handleSaveSequence}
        />
      ) : null}
      {addBomTagsModal && <BomLibraryTagsModel onOpen={addBomTagsModal} toggle={toggleBomTagsModel} BOMId={selectedTagId} />}
    </>
  );
}

export default AddEditBomLibraryGroup;
